import { Component, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { logReactError } from "@/lib/errorLogger";
import { isChunkLoadError } from "@/lib/chunkLoadError";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Marks that this browsing session has already spent its one automatic
 * reload. See the reload-guard comment in componentDidCatch.
 */
const CHUNK_RELOAD_FLAG = "chunk-error-reload";

interface Props {
  children: ReactNode;
  /** Optional callback when user clicks "Try Again" - if not provided, page will reload */
  onReset?: () => void;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Optional title for the error message */
  title?: string;
  /** Optional description for the error message */
  description?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  /** True when the caught error is a post-deploy stale-chunk failure. */
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Pass the actual error so Sentry's captureConsoleIntegration captures
    // a real exception with stack rather than just the prefix string.
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack);

    // Recomputed rather than read from state: getDerivedStateFromError does
    // run first, but this path must not depend on React's phase ordering.
    const isChunkError = isChunkLoadError(error);

    // ── Stale-chunk recovery ────────────────────────────────────────────
    // A deploy replaced the hashed chunk this tab was about to import, so
    // the import 404s. A reload genuinely fixes it: the fresh document
    // references the hashes that exist now.
    //
    // AUTO-RELOAD vs BUTTON: auto-reload, exactly once per browsing
    // session, then a button forever after. Reasoning:
    //   - The user did not cause this and cannot diagnose it. Silently
    //     landing on the current version is the correct outcome, and the
    //     first attempt costs them nothing beyond a page load they were
    //     already mid-navigation for.
    //   - But a reload only helps if the chunk is genuinely missing. If
    //     the deploy itself is broken (bad chunk shipped, CDN serving the
    //     SPA document for /assets/*.js), the reload reproduces the same
    //     error and an ungated retry is an infinite reload loop, which is
    //     strictly worse than the broken screen it replaces.
    //
    // LOOP SAFETY is structural, not heuristic: the flag is written to
    // sessionStorage BEFORE window.location.reload() and is never cleared
    // for the life of the session. So at most ONE automatic navigation can
    // ever originate here per tab, whatever the error does afterwards.
    // Every subsequent chunk failure falls through to the render branch
    // below, which shows a one-click "Reload" card instead.
    //
    // That is new as of 2026-08-02 (S16-AUD-127). Two places used to clear
    // the flag, and between them the guard did not actually hold:
    //   1. HERE, on any non-chunk error. Wrong signal: an unrelated render
    //      error is no evidence that chunk loading recovered, so
    //      error-then-chunk-error could reload twice.
    //   2. src/App.tsx ErrorLoggerInit, "on successful app load". Worse:
    //      a successful mount of the MAIN bundle is exactly what happens on
    //      the reload this guard just triggered, so the flag was cleared
    //      before the same lazy import could fail again. On a deploy that
    //      ships a genuinely bad chunk that was an unbounded reload loop.
    // Cost of dropping both: a session that spans two deploys auto-heals on
    // the first and gets the Reload card on the second. Worth it.
    if (isChunkError && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      window.location.reload();
      return;
    }

    // Stale-chunk failures are infrastructure, not app bugs, and both
    // telemetry sinks already drop them (src/instrument.ts beforeSend and
    // errorLogger's shouldSkipClientError, which share the pattern list in
    // @/lib/chunkLoadError). Returning here keeps them out of the Sentry
    // quota and the client_errors table at the source too.
    if (isChunkError) {
      return;
    }

    // Send the original Error to Sentry as a proper exception event with the
    // React component stack attached. Without this we'd only get the
    // captureConsoleIntegration breadcrumb, which Sentry summarises as
    // "[ErrorBoundary] Caught error:" with no actionable detail.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });

    // Also log to client_errors DB table for the in-app admin dashboard.
    logReactError(error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, isChunkError: false });
    if (this.props.onReset) {
      this.props.onReset();
    } else {
      window.location.reload();
    }
  };

  /** Always a real reload — onReset would just re-run the failing import. */
  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Stale chunk after a deploy. Reached only when the one automatic
      // reload for this session was already spent, so this is the manual
      // path. Deliberately not styled as an error: nothing is broken, the
      // page is just older than the server. It also ignores the title /
      // description props, which describe the app-error case.
      if (this.state.isChunkError) {
        return (
          <div className="min-h-[400px] flex items-center justify-center p-6">
            <div className="max-w-md w-full p-6 rounded-xl border border-border bg-muted/30 text-foreground text-center space-y-4">
              <div className="flex justify-center">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 text-primary" />
                </div>
              </div>
              <h1 className="text-xl font-bold font-archivo">A new version is available</h1>
              <p className="text-sm text-muted-foreground">
                Spotivibly updated while this page was open, so part of it could not load.
                Reload to get the latest version.
              </p>
              <div className="flex gap-2 justify-center pt-2">
                <button
                  className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                  onClick={this.handleReload}
                >
                  <RefreshCw className="h-4 w-4" />
                  Reload
                </button>
              </div>
            </div>
          </div>
        );
      }

      const title = this.props.title || "Something went wrong";
      const description = this.props.description ||
        "The page encountered an unexpected error. Try again or refresh the page. If the issue persists, please let us know.";

      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <div className="max-w-md w-full p-6 rounded-xl border border-destructive/30 bg-destructive/5 text-foreground text-center space-y-4">
            <div className="flex justify-center">
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
            </div>
            <h1 className="text-xl font-bold font-archivo">{title}</h1>
            <p className="text-sm text-muted-foreground">{description}</p>
            {this.state.error && (
              <details className="text-left text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                <summary className="cursor-pointer font-medium focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus:outline-none">Technical details</summary>
                <pre className="mt-2 whitespace-pre-wrap wrap-break-word overflow-auto max-h-32">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex gap-2 justify-center pt-2">
              <button
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                onClick={this.handleReset}
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
